/**
 * Het profiel van de bewoner opnieuw ophalen (specs/20261016-taal-bewonersapp, T024/T024a,
 * FR-3, O-4).
 *
 * Twee aanleidingen:
 *   • de opdracht `profile_refresh` (MQTT of websocket, zelfde `dispatchCommand`), die de API
 *     stuurt na een wijziging van de taal van de bewoner of van de site;
 *   • terugkeer naar de voorgrond — gedempt tot hoogstens één keer per 60 s, zodat een
 *     kiosk-tablet die flikkert de API niet bestookt. De push negeert die demping.
 *
 * ── NOOIT UITLOGGEN ─────────────────────────────────────────────────────────
 *
 * Een fout verandert niets: de huidige taal blijft. Een 401 door een DB-storing logde ooit
 * de hele vloot uit (verifyToken slikt DB-fouten in tot 401). Daarom gaat dit verzoek met
 * `_retry: true` door de ENE API-laag van de app: de interceptor van `createApiClient` ziet
 * het dan als "al geprobeerd" en start geen refresh en geen `onAuthFailed`.
 *
 * ── SAMENVOEGEN, NIET VERVANGEN ─────────────────────────────────────────────
 *
 * De login-sessie draagt velden (rol, permissies, person.location …) die het profiel-
 * endpoint niet kent. Vervangen zou ze wissen.
 */
import { useEffect } from 'react';
import { API_ENDPOINTS } from '@caro/shared/constants';
import { api, authStore } from './useAuth';
import { NativeBridge, isNative } from '../native/NativeBridge';
import * as VoiceCache from '../services/VoiceCache';

const FOREGROUND_THROTTLE_MS = 60_000;
const PROFILE_URL: string = (API_ENDPOINTS as Record<string, unknown>).USER_PROFILE as string
  || '/api/v1/user/profile';

let lastForegroundAt = 0;
let inFlight: Promise<boolean> | null = null;

function merge(current: any, profile: any): any {
  const currentPerson = current && current.person && typeof current.person === 'object' ? current.person : {};
  const profilePerson = profile && profile.person && typeof profile.person === 'object' ? profile.person : null;
  return {
    ...current,
    ...profile,
    person: profilePerson ? { ...currentPerson, ...profilePerson } : current.person,
  };
}

/** Haal het profiel op en voeg het samen met de huidige user. `true` = bijgewerkt. */
export function refetchProfile(): Promise<boolean> {
  if (inFlight) return inFlight;
  const current = authStore.getState().user;
  if (!current) return Promise.resolve(false); // niet aangemeld: nooit iemand aanmelden

  inFlight = (async () => {
    try {
      const res: any = await api.get(PROFILE_URL, { _retry: true } as any);
      const profile = res && res.success !== false ? res.user : null;
      if (!profile || typeof profile !== 'object') return false;
      const latest = authStore.getState().user;
      if (!latest) return false; // intussen uitgelogd
      authStore.getState().setUser(merge(latest, profile));
      // Spraakmeldingen fase 1 (T091, FR-2.5): `profile_refresh` komt ook na een nieuwe
      // platformstem of prerender; de voorlaadlijst ververst mee. Een taalwissel laadt
      // bovendien via useNotificationTransport in de nieuwe taal. Nooit geawait.
      void VoiceCache.refresh();
      return true;
    } catch (e: any) {
      // Alleen loggen. Niets wijzigen, niet uitloggen.
      console.warn('[ProfileRefresh] ophalen mislukt:', e?.response?.status ?? e?.message ?? e);
      return false;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Terugkeer naar de voorgrond: gedempt tot één verzoek per 60 s. */
export function refreshProfileOnForeground(): Promise<boolean> {
  const now = Date.now();
  if (now - lastForegroundAt < FOREGROUND_THROTTLE_MS) return Promise.resolve(false);
  lastForegroundAt = now;
  return refetchProfile();
}

/** Alleen voor tests. */
export function __resetProfileRefresh(): void {
  lastForegroundAt = 0;
  inFlight = null;
}

/**
 * Luistert naar terugkeer naar de voorgrond: `visibilitychange` in de WebView/browser en
 * Capacitor `appStateChange` op de APK. Mounten zolang de sessie aangemeld is.
 */
export function useProfileRefreshOnForeground(): void {
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshProfileOnForeground();
    };
    document.addEventListener('visibilitychange', onVisible);

    let handle: { remove: () => void } | null = null;
    let cancelled = false;
    if (isNative) {
      try {
        Promise.resolve(NativeBridge.onAppStateChange((active) => {
          if (active) void refreshProfileOnForeground();
        })).then((h: any) => {
          if (cancelled) h?.remove?.();
          else handle = h;
        }).catch(() => { /* geen Capacitor-app-plugin: alleen visibilitychange */ });
      } catch {
        /* idem */
      }
    }
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      handle?.remove();
    };
  }, []);
}
